import { useEffect, useRef } from 'react'
import { consumer } from './cable'
import type { Subscription } from '@rails/actioncable'
import type { AgentSessionProps, TodoProps, ToolCallProps, ToolCallUpdatedPayload } from '@/types'

export type ChatStreamCallbacks = {
  onContent: (content: string) => void
  onToolCall: (toolCall: ToolCallProps) => void
  onToolCallUpdated: (payload: ToolCallUpdatedPayload) => void
  onSessionUpdated: (session: AgentSessionProps) => void
  onTodosUpdated: (todos: TodoProps[]) => void
  onError: (error: string) => void
  onDone: () => void
}

export function useChatStream(
  sessionId: string | null,
  active: boolean,
  callbacks: ChatStreamCallbacks
) {
  const callbacksRef = useRef(callbacks)
  callbacksRef.current = callbacks

  useEffect(() => {
    if (!sessionId || !active) return

    const subscription: Subscription = consumer.subscriptions.create(
      { channel: 'ChatStreamChannel', session_id: sessionId },
      {
        received(data: Record<string, unknown>) {
          const cb = callbacksRef.current
          switch (data.type) {
            case 'content':
              cb.onContent(data.content as string)
              break
            case 'tool_call':
              cb.onToolCall(data as unknown as ToolCallProps)
              break
            case 'tool_call_updated':
              cb.onToolCallUpdated(data as unknown as ToolCallUpdatedPayload)
              break
            case 'session_updated':
              cb.onSessionUpdated(data.session as AgentSessionProps)
              break
            case 'todos_updated':
              cb.onTodosUpdated(data.todos as TodoProps[])
              break
            case 'error':
              cb.onError(data.error as string)
              break
            case 'done':
              cb.onDone()
              break
          }
        },
      }
    )

    return () => {
      subscription.unsubscribe()
    }
  }, [sessionId, active])
}
